/* MRO UGC rail: slow crawl + Shop this look popup.

   Self-contained on purpose. It never touches mro.js state and it builds its
   own modal under .mro-lookmodal, because mro.js removes stray .mro-modal
   nodes the first time it builds its own.

   Crawl notes. Every one of these was a real bug in the first version:

   - The drift keeps its own float position and WRITES scrollLeft from it.
     The old code did rail.scrollLeft += 0.42 every frame, and the browser
     rounds that property to whole pixels, so the increment was thrown away
     and the rail sat perfectly still at any speed under about 30px a second.
   - One lap is measured from the first clone, not scrollWidth / 2. Half the
     flex gap fell outside that estimate, so the loop jumped sideways at the
     seam on every pass.
   - Hover pause runs off pointer events and ignores touch. A tap on a phone
     fires mouseenter and never fires mouseleave, which froze the rail for
     good after the first tap.
   - Focus only pauses for keyboard focus. Closing a look hands focus back to
     the tile that opened it, which used to pause the rail forever.
   - Wrapping also runs after a finger scroll settles, so swiping never hits a
     dead end, and it never runs mid touch where it would kill momentum.
   - Sizes are measured on resize, not on every frame, and the loop stops when
     the rail is off screen or the tab is hidden.
*/
(function () {
  'use strict';

  if (window.__MRO_UGC__) return;
  window.__MRO_UGC__ = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function track(name, payload) {
    payload = payload || {};
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload));
    } catch (e) {}
    try { if (typeof gtag === 'function') gtag('event', name, payload); } catch (e) {}
  }

  /* ---------------- modal ---------------- */
  var modal, box, closeBtn, lastFocus, openedAt = 0;

  function buildModal() {
    if (modal) return;
    modal = document.createElement('div');
    modal.className = 'mro-lookmodal';
    modal.setAttribute('hidden', '');
    modal.innerHTML =
      '<button class="mro-lookmodal-scrim" type="button" data-look-close aria-label="Close"></button>' +
      '<div class="mro-lookmodal-shell">' +
        '<button class="mro-lookmodal-close" type="button" data-look-close>Close</button>' +
        '<div class="mro-lookmodal-box" role="dialog" aria-modal="true" aria-label="Shop this look"></div>' +
      '</div>';
    document.body.appendChild(modal);
    box = modal.querySelector('.mro-lookmodal-box');
    closeBtn = modal.querySelector('.mro-lookmodal-close');

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-look-close]')) {
        e.preventDefault();
        closeLook();
      }
    });
  }

  function isOpen() { return modal && !modal.hasAttribute('hidden'); }

  function openLook(html, label) {
    buildModal();
    lastFocus = document.activeElement;
    openedAt = Date.now();
    box.innerHTML = html;
    box.setAttribute('aria-label', label || 'Shop this look');
    box.scrollTop = 0;
    modal.removeAttribute('hidden');
    document.body.classList.add('mro-look-locked');
    requestAnimationFrame(function () { modal.classList.add('is-on'); });
    if (closeBtn) { try { closeBtn.focus(); } catch (e) {} }
  }

  function closeLook() {
    if (!isOpen()) return;
    modal.classList.remove('is-on');
    document.body.classList.remove('mro-look-locked');
    setTimeout(function () {
      modal.setAttribute('hidden', '');
      box.innerHTML = '';
    }, 260);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeLook();
  });

  /* ---------------- rail ---------------- */
  function initRails(scope) {
    (scope || document).querySelectorAll('[data-ugc-rail]').forEach(function (rail) {
      if (rail._ugcReady) return;
      rail._ugcReady = true;
      setupRail(rail);
    });
  }

  function setupRail(rail) {
    var wrap = rail.closest('[data-ugc-wrap]');
    var section = rail.closest('[data-ugc]');
    if (!wrap || !section) return;

    var prog = section.querySelector('[data-ugc-prog]');
    var crawl = rail.getAttribute('data-crawl') === '1' && !REDUCED;
    var speed = Math.abs(parseFloat(rail.getAttribute('data-speed') || '17')) || 17;
    var dir = rail.getAttribute('data-dir') === 'right' ? -1 : 1;

    /* Keep a horizontal swipe on the rail from arming the browser back gesture. */
    rail.style.overscrollBehaviorX = 'contain';

    /* Clone the tiles once so the wrap at the seam is invisible. Clones are
       hidden from assistive tech and skipped by the tab order. */
    var originals = [].slice.call(rail.children);
    if (crawl) {
      originals.forEach(function (el) {
        var c = el.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('tabindex', '-1');
        c.setAttribute('data-ugc-clone', '');
        rail.appendChild(c);
      });
    }

    var period = 0;   /* exact width of one full set of tiles, gap included */
    var low = 0;      /* bottom of the band the position is kept inside */
    var maxScroll = 0;
    var progRoom = 0; /* how far the progress thumb can travel, in its own widths */
    var pos = rail.scrollLeft;
    var written = pos;
    var paused = false, touching = false, holdUntil = 0;
    var running = false, onScreen = true, raf = 0, last = 0;
    var settleT = 0, sizeT = 0;

    function hold(ms) { holdUntil = Date.now() + (ms || 3000); }

    function norm(x) {
      if (period <= 0) return x;
      while (x < low) x += period;
      while (x >= low + period) x -= period;
      return x;
    }

    function write(x) {
      written = x;
      rail.scrollLeft = x;
    }

    function measure() {
      maxScroll = rail.scrollWidth - rail.clientWidth;
      if (maxScroll < 0) maxScroll = 0;

      if (prog) {
        var thumb = prog.offsetWidth || 1;
        var trackW = (prog.parentNode && prog.parentNode.clientWidth) || thumb;
        progRoom = Math.max(0, trackW - thumb) / thumb;
      }

      period = 0;
      low = 0;
      if (!crawl) return;

      var firstClone = rail.querySelector('[data-ugc-clone]');
      if (firstClone && originals[0]) {
        period = firstClone.offsetLeft - originals[0].offsetLeft;
      }
      if (!(period > 0)) { period = 0; return; }

      /* Park the working band in the middle of the two copies when there is
         room, so a swipe has slack in both directions, and never let the band
         run past the end of the cloned copy. */
      low = Math.max(0, Math.min(period / 2, maxScroll - period));
      pos = norm(pos);
    }

    function paint() {
      if (!prog) return;
      var span = period > 0 ? period : maxScroll;
      if (span <= 0) return;
      var at = (((pos - low) % span) + span) % span / span;
      prog.style.transform = 'translateX(' + (at * progRoom * 100) + '%)';
    }

    function tick(ts) {
      if (!rail.isConnected) { stop(); return; }
      raf = requestAnimationFrame(tick);

      if (!last) last = ts;
      var dt = ts - last;
      last = ts;
      if (dt < 0) dt = 0;
      if (dt > 60) dt = 60;

      if (period > 0 && !paused && !touching && !isOpen() && Date.now() > holdUntil) {
        pos = norm(pos + dir * speed * (dt / 1000));
        write(pos);
        paint();
      }
    }

    function play() {
      if (running || !crawl) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(tick);
    }

    function stop() {
      if (!running) return;
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    function sync() {
      if (crawl && onScreen && !document.hidden) play();
      else stop();
    }

    /* A finger scroll leaves our float behind, so pick the real value back up,
       then wrap once the scroll has settled and no finger is down. */
    function settle() {
      if (touching || period <= 0) return;
      var next = norm(pos);
      if (Math.abs(next - pos) > 0.5) {
        pos = next;
        write(next);
      }
      paint();
    }

    rail.addEventListener('scroll', function () {
      var at = rail.scrollLeft;
      if (Math.abs(at - written) > 2) {
        pos = at;
        written = at;
        paint();
      }
      clearTimeout(settleT);
      settleT = setTimeout(settle, 160);
    }, { passive: true });

    /* Hover pause, mouse only. Touch fires pointerenter with no matching
       pointerleave, which is what used to freeze the rail on phones. */
    if (window.PointerEvent) {
      wrap.addEventListener('pointerenter', function (e) {
        if (!e.pointerType || e.pointerType === 'mouse') paused = true;
      });
      wrap.addEventListener('pointerleave', function (e) {
        if (!e.pointerType || e.pointerType === 'mouse') { paused = false; hold(600); }
      });
    } else {
      wrap.addEventListener('mouseenter', function () { paused = true; });
      wrap.addEventListener('mouseleave', function () { paused = false; hold(600); });
    }

    wrap.addEventListener('touchstart', function () { touching = true; hold(3000); }, { passive: true });
    function endTouch() { touching = false; hold(2500); }
    wrap.addEventListener('touchend', endTouch, { passive: true });
    wrap.addEventListener('touchcancel', endTouch, { passive: true });

    /* Only keyboard focus pauses. Closing a look returns focus to the tile,
       and a plain focus pause never released it. */
    wrap.addEventListener('focusin', function (e) {
      var keyboard = true;
      try { keyboard = e.target.matches(':focus-visible'); } catch (err) { keyboard = true; }
      if (keyboard) paused = true;
    });
    wrap.addEventListener('focusout', function () { paused = false; hold(1500); });

    function page(step) {
      hold(4000);
      var jump = Math.max(140, Math.round(rail.clientWidth * 0.85));
      var target = pos + step * jump;

      if (period > 0 && (target < 0 || target > maxScroll)) {
        /* Hop a whole copy first. Identical tiles, so nothing visibly moves,
           and the arrows stop dead ending at the ends of the rail. */
        var shift = target < 0 ? period : -period;
        pos = pos + shift;
        write(pos);
        target += shift;
      }

      if (target < 0) target = 0;
      if (target > maxScroll) target = maxScroll;
      rail.scrollTo({ left: target, behavior: REDUCED ? 'auto' : 'smooth' });
    }

    var prev = section.querySelector('[data-ugc-prev]');
    var next = section.querySelector('[data-ugc-next]');
    if (prev) prev.addEventListener('click', function () { page(-1); });
    if (next) next.addEventListener('click', function () { page(1); });

    function remeasure() {
      clearTimeout(sizeT);
      sizeT = setTimeout(function () {
        measure();
        if (period > 0) write(pos);
        paint();
      }, 120);
    }

    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);
    window.addEventListener('load', remeasure);
    if (window.ResizeObserver) {
      try { new ResizeObserver(remeasure).observe(rail); } catch (e) {}
    }

    if (window.IntersectionObserver) {
      try {
        new IntersectionObserver(function (entries) {
          onScreen = entries[entries.length - 1].isIntersecting;
          sync();
        }, { rootMargin: '150px' }).observe(section);
      } catch (e) {}
    }
    document.addEventListener('visibilitychange', sync);

    measure();
    if (period > 0) write(pos);
    paint();
    sync();
  }

  /* ---------------- open a look ---------------- */
  document.addEventListener('click', function (e) {
    if (isOpen() && modal.contains(e.target)) {
      /* swallow the stray click that can land as the sheet slides up */
      if (Date.now() - openedAt < 350) { e.preventDefault(); }
      return;
    }

    var tile = e.target.closest('[data-ugc-tile]');
    if (!tile || tile.disabled) return;
    e.preventDefault();

    var id = tile.getAttribute('data-ugc-tile');
    var host = document.querySelector('[data-ugc-pop="' + id + '"]');
    if (!host) return;

    openLook(host.innerHTML, tile.getAttribute('data-look-label') || 'Shop this look');
    track('ugc_look_opened', {
      look_name: tile.getAttribute('data-look-name') || '',
      pieces: Number(tile.getAttribute('data-look-count') || 0)
    });
  });

  /* ---------------- boot ---------------- */
  function start() { initRails(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (e) { initRails(e.target); });

  window.MRO_UGC = { open: openLook, close: closeLook, boot: initRails };
})();